import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const locationKey = location.key;
  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey]);

  const navLinks = [
    { path: '/', label: 'Startseite' },
    { path: '/leistungen', label: 'Leistungen' },
    { path: '/ueber-uns', label: 'Über uns' },
    { path: '/kontakt', label: 'Kontakt' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <a href="#main-content" className="skip-link">
        Zum Hauptinhalt springen
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5' 
            : 'bg-transparent'
        }`}
        data-testid="main-header"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center group"
              data-testid="logo-link"
              aria-label="Phönix Pflege Team Itzehoe - Startseite"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_98872b8f-1d95-46de-bdd8-15acab8c6ffc/artifacts/5h271v8q_0A4AD33B-1D8D-4D91-84DE-B5E3E6EA9948.png"
                alt="Phönix Pflege Team Itzehoe Logo"
                className="h-12 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Hauptnavigation">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive(link.path)
                      ? 'text-[#1A52D8] bg-[#1A52D8]/5'
                      : 'text-[#64748b] hover:text-[#1A52D8] hover:bg-[#1A52D8]/5'
                  }`}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Phone CTA - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+4915146173685"
                className="flex items-center gap-2 text-[#64748b] hover:text-[#1A52D8] transition-colors duration-300"
                data-testid="header-phone-link"
                aria-label="Jetzt anrufen"
              >
                <Phone className="w-4 h-4" strokeWidth={2} />
                <span className="text-sm font-medium">Jetzt Anrufen</span>
              </a>
              <Link to="/kontakt">
                <Button 
                  className="bg-[#1A52D8] text-white hover:bg-[#1545b8] h-11 px-6 rounded-full text-sm font-semibold shadow-lg shadow-[#1A52D8]/20 hover:shadow-xl transition-all duration-300"
                  data-testid="header-contact-btn"
                >
                  Kontakt
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full bg-[#1A52D8]/5 text-[#1A52D8] hover:bg-[#1A52D8]/10 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={isMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden fixed inset-x-0 top-20 bg-white border-t border-[#e2e8f0] shadow-2xl transition-all duration-300 ${
            isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          data-testid="mobile-menu"
        >
          <nav className="container-custom py-6" aria-label="Mobile Navigation">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-xl text-lg font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-[#1A52D8] bg-[#1A52D8]/5'
                      : 'text-[#1e293b] hover:bg-[#f8fafb]'
                  }`}
                  data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            <div className="mt-6 pt-6 border-t border-[#e2e8f0] space-y-3">
              <a
                href="tel:+4915146173685"
                className="flex items-center justify-center gap-3 h-14 bg-[#1A52D8] text-white rounded-xl text-lg font-semibold shadow-lg"
                data-testid="mobile-phone-link"
              >
                <Phone className="w-5 h-5" />
                Jetzt Anrufen
              </a>
              <Link to="/kontakt" className="block">
                <Button 
                  className="w-full h-14 bg-[#1A52D8]/5 text-[#1A52D8] hover:bg-[#1A52D8]/10 rounded-xl text-lg font-semibold"
                  data-testid="mobile-contact-btn"
                >
                  Nachricht senden
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};
